import { useState, useEffect } from 'react';
import { SlidersHorizontal, Plus, Loader2, Edit, Trash2, Save, X, Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { slaAPI } from '../services/api';
import { toast } from 'sonner';

const TICKET_TYPES = [
  { value: 'incident', label: 'Incident', priorities: ['p1', 'p2', 'p3', 'p4'] },
  { value: 'service_request', label: 'Service Request', priorities: ['asap', 'this_week', 'standard', 'no_rush'] },
  { value: 'change_request', label: 'Change Request', priorities: ['immediate', 'high', 'medium', 'low'] },
];
const ALL_PRIORITIES = ['p1', 'p2', 'p3', 'p4', 'asap', 'this_week', 'standard', 'no_rush', 'immediate', 'high', 'medium', 'low'];

function fmtTime(m) {
  if (!m) return '-';
  if (m >= 1440) return `${Math.round(m / 1440)}d`;
  if (m >= 60) return `${Math.round(m / 60)}h`;
  return `${m}m`;
}

// ─── Global SLA Policies tab ────────────────────────────────────────────────
function GlobalPoliciesTab() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', ticket_type: 'incident', priority: 'p1', response_time_minutes: 15, resolution_time_minutes: 240, is_active: true });

  const load = async () => {
    setLoading(true);
    try { const r = await slaAPI.listPolicies(); setPolicies(r.data?.results || r.data || []); }
    catch { setPolicies([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSave = async () => {
    try {
      if (editing) { await slaAPI.updatePolicy(editing, form); toast.success('Policy updated'); }
      else { await slaAPI.createPolicy(form); toast.success('Policy created'); }
      setEditing(null); setShowAdd(false);
      setForm({ name: '', ticket_type: 'incident', priority: 'p1', response_time_minutes: 15, resolution_time_minutes: 240, is_active: true });
      load();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this SLA policy?')) return;
    try { await slaAPI.deletePolicy(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { setShowAdd(true); setEditing(null); setForm({ name: '', ticket_type: 'incident', priority: 'p1', response_time_minutes: 15, resolution_time_minutes: 240, is_active: true }); }}
          className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="add-sla-btn">
          <Plus className="w-4 h-4" /> Add Global Policy
        </button>
      </div>
      {showAdd && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6" data-testid="sla-form">
          <h3 className="font-semibold mb-4 dark:text-white">{editing ? 'Edit' : 'New'} Global SLA Policy</h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <input placeholder="Policy Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <select value={form.ticket_type} onChange={e => setForm({...form, ticket_type: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
              <option value="incident">Incident</option>
              <option value="service_request">Service Request</option>
              <option value="change_request">Change Request</option>
            </select>
            <input placeholder="Priority (e.g. p1)" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <input type="number" placeholder="Response (min)" value={form.response_time_minutes} onChange={e => setForm({...form, response_time_minutes: parseInt(e.target.value) || 0})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
            <input type="number" placeholder="Resolution (min)" value={form.resolution_time_minutes} onChange={e => setForm({...form, resolution_time_minutes: parseInt(e.target.value) || 0})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
          </div>
          <div className="flex items-center gap-4 mt-3">
            <label className="flex items-center gap-2 text-sm dark:text-slate-300"><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="rounded" /> Active</label>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-sky-600"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => { setShowAdd(false); setEditing(null); }} className="bg-slate-100 text-slate-600 px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-slate-200"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}
      {loading ? <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div> : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
          <table className="w-full" data-testid="sla-table">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Policy Name</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>
                <th className="px-4 py-3 text-left font-semibold">Priority</th>
                <th className="px-4 py-3 text-center font-semibold">Response</th>
                <th className="px-4 py-3 text-center font-semibold">Resolution</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700 text-sm">
              {policies.map(p => (
                <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <td className="px-4 py-3 font-medium dark:text-white">{p.name}</td>
                  <td className="px-4 py-3 dark:text-slate-300">{p.ticket_type?.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3"><span className="bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 px-2 py-1 rounded-full text-xs font-semibold">{p.priority}</span></td>
                  <td className="px-4 py-3 text-center font-medium dark:text-white">{fmtTime(p.response_time_minutes)}</td>
                  <td className="px-4 py-3 text-center font-medium dark:text-white">{fmtTime(p.resolution_time_minutes)}</td>
                  <td className="px-4 py-3 text-center"><span className={`px-2 py-1 rounded-full text-xs ${p.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => { setForm({ name: p.name, ticket_type: p.ticket_type, priority: p.priority, response_time_minutes: p.response_time_minutes, resolution_time_minutes: p.resolution_time_minutes, is_active: p.is_active }); setEditing(p.id); setShowAdd(true); }} className="text-slate-400 hover:text-sky-600 p-1"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {policies.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No global SLA policies configured</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Empty entries template for a new Policy Set ───────────────────────────
function buildEmptyEntries() {
  const entries = [];
  for (const tt of TICKET_TYPES) {
    for (const pri of tt.priorities) {
      entries.push({ ticket_type: tt.value, priority: pri, response_time_minutes: 30, resolution_time_minutes: 240 });
    }
  }
  return entries;
}

// ─── SLA Policy Sets tab ────────────────────────────────────────────────────
function PolicySetsTab() {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const emptyForm = { name: '', description: '', is_active: true, entries: buildEmptyEntries() };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try { const r = await slaAPI.listPolicySets(); setSets(r.data?.results || r.data || []); }
    catch { setSets([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const updateEntry = (idx, field, val) => {
    const entries = [...form.entries];
    entries[idx] = { ...entries[idx], [field]: parseInt(val) || 0 };
    setForm({ ...form, entries });
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Policy set name is required'); return; }
    try {
      if (editing) { await slaAPI.updatePolicySet(editing, form); toast.success('Policy set updated'); }
      else { await slaAPI.createPolicySet(form); toast.success('Policy set created'); }
      setShowForm(false); setEditing(null); setForm(emptyForm); load();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this SLA policy set? Applications using it will fall back to global SLA policies.')) return;
    try { await slaAPI.deletePolicySet(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const startEdit = (ps) => {
    const entries = buildEmptyEntries().map(tmpl => {
      const existing = (ps.entries || []).find(e => e.ticket_type === tmpl.ticket_type && e.priority === tmpl.priority);
      return existing ? { ticket_type: existing.ticket_type, priority: existing.priority, response_time_minutes: existing.response_time_minutes, resolution_time_minutes: existing.resolution_time_minutes } : tmpl;
    });
    setForm({ name: ps.name, description: ps.description || '', is_active: ps.is_active, entries });
    setEditing(ps.id); setShowForm(true);
  };

  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  return (
    <div>
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4 text-sm text-amber-800 dark:text-amber-300">
        <strong>How it works:</strong> Assign a Policy Set to an Application (on the Applications page). When a ticket is created for that application, its SLA timings come from this set instead of the global defaults.
      </div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm); }}
          className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="add-policy-set-btn">
          <Plus className="w-4 h-4" /> New Policy Set
        </button>
      </div>

      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6" data-testid="policy-set-form">
          <h3 className="font-semibold mb-4 dark:text-white">{editing ? 'Edit' : 'New'} SLA Policy Set</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <input placeholder="Policy Set Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="Description (optional)" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
          </div>

          {TICKET_TYPES.map(tt => (
            <div key={tt.value} className="mb-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">{tt.label}</h4>
              <div className="bg-slate-50 dark:bg-slate-900 rounded-xl border dark:border-slate-700 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="px-4 py-2 text-left">Priority</th>
                      <th className="px-4 py-2 text-center">Response Time (min)</th>
                      <th className="px-4 py-2 text-center">Resolution Time (min)</th>
                      <th className="px-4 py-2 text-center">Preview</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-700">
                    {form.entries.filter(e => e.ticket_type === tt.value).map((entry, i) => {
                      const globalIdx = form.entries.findIndex(e => e.ticket_type === tt.value && e.priority === entry.priority);
                      return (
                        <tr key={entry.priority}>
                          <td className="px-4 py-2"><span className="bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 px-2 py-0.5 rounded-full text-xs font-semibold">{entry.priority}</span></td>
                          <td className="px-4 py-2 text-center"><input type="number" min="1" value={entry.response_time_minutes} onChange={e => updateEntry(globalIdx, 'response_time_minutes', e.target.value)} className="w-24 border dark:border-slate-600 rounded-lg px-2 py-1 text-sm text-center dark:bg-slate-800 dark:text-white" /></td>
                          <td className="px-4 py-2 text-center"><input type="number" min="1" value={entry.resolution_time_minutes} onChange={e => updateEntry(globalIdx, 'resolution_time_minutes', e.target.value)} className="w-24 border dark:border-slate-600 rounded-lg px-2 py-1 text-sm text-center dark:bg-slate-800 dark:text-white" /></td>
                          <td className="px-4 py-2 text-center text-xs text-slate-400">{fmtTime(entry.response_time_minutes)} / {fmtTime(entry.resolution_time_minutes)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-sky-600"><Save className="w-4 h-4" /> Save Policy Set</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm flex items-center gap-1.5"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div> : (
        <div className="space-y-3">
          {sets.map(ps => (
            <div key={ps.id} className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden" data-testid={`policy-set-${ps.id}`}>
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <button onClick={() => toggleExpand(ps.id)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                    {expanded[ps.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div>
                    <div className="font-semibold dark:text-white flex items-center gap-2">
                      <Layers className="w-4 h-4 text-sky-500" />{ps.name}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${ps.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{ps.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                    {ps.description && <div className="text-xs text-slate-400 mt-0.5">{ps.description}</div>}
                    <div className="text-xs text-slate-400 mt-0.5">{ps.entries?.length || 0} SLA entries</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(ps)} className="text-slate-400 hover:text-sky-600 p-1.5 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-900/20"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(ps.id)} className="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {expanded[ps.id] && (
                <div className="border-t dark:border-slate-700 px-4 py-3">
                  {TICKET_TYPES.map(tt => {
                    const entries = (ps.entries || []).filter(e => e.ticket_type === tt.value);
                    if (!entries.length) return null;
                    return (
                      <div key={tt.value} className="mb-3 last:mb-0">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">{tt.label}</div>
                        <div className="flex flex-wrap gap-2">
                          {entries.map(e => (
                            <div key={e.priority} className="bg-slate-50 dark:bg-slate-700 rounded-lg px-3 py-1.5 text-xs">
                              <span className="font-semibold text-sky-700 dark:text-sky-300">{e.priority}</span>
                              <span className="text-slate-400 mx-1">·</span>
                              <span className="text-slate-600 dark:text-slate-300">R:{fmtTime(e.response_time_minutes)}</span>
                              <span className="text-slate-400 mx-1">/</span>
                              <span className="text-slate-600 dark:text-slate-300">Res:{fmtTime(e.resolution_time_minutes)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {sets.length === 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 px-4 py-10 text-center text-slate-400">
              No application-specific SLA policy sets yet. Create one and assign it to an application.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────
export default function SLAPoliciesPage() {
  const [tab, setTab] = useState('sets');

  return (
    <div data-testid="sla-policies-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <SlidersHorizontal className="w-6 h-6 text-amber-600" /> SLA Policies
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl w-fit">
        <button onClick={() => setTab('sets')} data-testid="tab-policy-sets"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'sets' ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>
          <Layers className="w-4 h-4 inline mr-1.5" />App-Specific Policy Sets
        </button>
        <button onClick={() => setTab('global')} data-testid="tab-global-policies"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'global' ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}>
          <SlidersHorizontal className="w-4 h-4 inline mr-1.5" />Global Defaults
        </button>
      </div>

      {tab === 'sets' ? <PolicySetsTab /> : <GlobalPoliciesTab />}
    </div>
  );
}
